import { useState } from 'react';
import Button from '../../../components/base/Button';
import AppointmentModal from '../../../components/feature/AppointmentModal';

export default function Process() {
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      id: 1,
      title: 'Keşif & Değerlendirme',
      description: 'İşletmenizin mevcut sürdürülebilirlik durumunu, hedeflerini ve beklentilerini detaylı olarak analiz ediyoruz.',
      details: [
        'Mevcut sürdürülebilirlik durumu analizi ve benchmark',
        'Paydaş analizi ve beklenti yönetimi stratejisi',
        'Rekabet analizi ve sektörel karşılaştırma',
        'İlk değerlendirme raporu ve öneriler'
      ],
      icon: 'ri-search-2-line',
      duration: '1-2 Hafta',
      importance: 'Doğru başlangıç, başarılı bir sürdürülebilirlik yolculuğunun temelidir. Mevcut durumunuzu anlamadan hedef belirlemek mümkün değildir. Bu aşamada şirketinizin sürdürülebilirlik olgunluk seviyesini belirliyoruz.'
    },
    {
      id: 2,
      title: 'Derinlemesine GAP Analizi',
      description: 'Sürdürülebilirlik performansınızı ölçüyor, riskleri ve fırsatları belirliyoruz.',
      details: [
        'Çevresel etki değerlendirmesi (LCA metodolojisi)',
        'Sosyal etki analizi ve toplum katkı ölçümü',
        'Yönetişim yapısı incelemesi ve ESG skoru',
        'Risk ve fırsat matrisi ile öncelik belirleme'
      ],
      icon: 'ri-bar-chart-line',
      duration: '2-3 Hafta',
      importance: 'Detaylı analiz olmadan yapılan iyileştirmeler genellikle yetersiz kalır. Veriye dayalı kararlar sürdürülebilirlik başarısının anahtarıdır. GRI, TSRS ve CSRD gerekliliklerine uyum için kritik aşama.'
    },
    {
      id: 3,
      title: 'Strateji & Yol Haritası',
      description: 'Size özel sürdürülebilirlik stratejisi ve detaylı yol haritası geliştiriyoruz.',
      details: [
        'Sürdürülebilirlik stratejisi ve vizyonu oluşturma',
        'SMART hedef belirleme ve KPI tanımlama',
        'Detaylı yol haritası ve zaman planı',
        'Bütçe ve kaynak planlaması, ROI hesaplama'
      ],
      icon: 'ri-route-line',
      duration: '2-4 Hafta',
      importance: 'Stratejisiz hareket etmek kaynak israfına yol açar. Net hedefler ve planlar olmadan sürdürülebilirlik çabaları dağınık kalır. SBTi ve Net Zero hedefleri bu aşamada belirlenir.'
    },
    {
      id: 4,
      title: 'Uygulama & İzleme',
      description: 'Belirlenen stratejileri hayata geçirmek için pratik adımları birlikte atıyoruz.',
      details: [
        'Proje yönetimi ve koordinasyon hizmetleri',
        'Ekip eğitimleri ve kapasite geliştirme programları',
        'Süreç iyileştirmeleri ve sistem entegrasyonu',
        'Düzenli takip, gözden geçirme ve revizyon'
      ],
      icon: 'ri-settings-4-line',
      duration: '3-6 Ay',
      importance: 'En iyi strateji bile uygulanmazsa değersizdir. Sistematik uygulama ve sürekli takip sürdürülebilirlik hedeflerine ulaşmayı garantiler. SUSTAINES yazılımı ile dijital takip sağlıyoruz.'
    },
    {
      id: 5,
      title: 'Raporlama & İletişim',
      description: 'GRI, CSRD, TSRS standartlarında profesyonel raporlar hazırlıyor ve paydaşlarla paylaşıyoruz.',
      details: [
        'GRI/TSRS/CSRD uyumlu sürdürülebilirlik raporu',
        'Veri doğrulama ve kalite kontrol (TÜV Austria)',
        'Görsel tasarım ve içerik optimizasyonu',
        'Paydaş sunumları ve iletişim stratejisi'
      ],
      icon: 'ri-file-chart-line',
      duration: '2-4 Hafta',
      importance: 'Sürdürülebilirlik performansını paydaşlarla paylaşmak şeffaflık sağlar ve itibarınızı güçlendirir. Profesyonel raporlama yasal zorunlulukları karşılar ve yatırımcı güvenini artırır.'
    }
  ];

  const handleCallRequest = async () => {
    // Bu fonksiyon "Sizi Arayalım" butonu için kullanılacak
    // Form modal açmak yerine direkt WhatsApp'a yönlendirme yapabiliriz
    const message = encodeURIComponent('Merhaba, sürdürülebilirlik süreciniz hakkında bilgi almak istiyorum. Beni aramanızı rica ediyorum.');
    window.open(`https://wa.me/905326230183?text=${message}`, '_blank');
  };

  return (
    <>
      <section className="py-20 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Sürdürülebilirlik Sürecimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Kanıtlanmış metodolojimizle sürdürülebilirlik hedeflerinize adım adım ulaşın
            </p>
          </div>

          {/* Interactive Steps */}
          <div className="relative mb-16">
            {/* Progress Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gray-200 transform -translate-y-1/2 rounded-full"></div>
            <div 
              className="hidden lg:block absolute top-1/2 left-0 h-1 bg-gradient-to-r from-emerald-500 to-emerald-600 transform -translate-y-1/2 rounded-full transition-all duration-1000"
              style={{ width: `${(activeStep + 1) * 20}%` }}
            ></div>

            {/* Steps Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {steps.map((step, index) => (
                <div 
                  key={step.id}
                  className={`relative text-center cursor-pointer group transition-all duration-300 ${
                    activeStep === index ? 'scale-105' : 'hover:scale-102'
                  }`}
                  onClick={() => setActiveStep(index)}
                >
                  <div className="flex flex-col items-center">
                    {/* Icon Circle */}
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 relative z-10 transition-all duration-300 ${
                      activeStep === index 
                        ? 'bg-emerald-600 shadow-lg shadow-emerald-200' 
                        : 'bg-emerald-500 group-hover:bg-emerald-600'
                    }`}>
                      <i className={`${step.icon} text-2xl text-white`}></i>
                    </div>
                    
                    {/* Step Number */}
                    <div className={`absolute top-8 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center z-20 transition-all duration-300 ${
                      activeStep === index 
                        ? 'bg-emerald-700 scale-110' 
                        : 'bg-emerald-600'
                    }`}>
                      {step.id.toString().padStart(2, '0')}
                    </div>

                    {/* Content */}
                    <h3 className={`text-xl font-semibold mb-3 transition-colors duration-300 ${
                      activeStep === index ? 'text-emerald-700' : 'text-gray-900'
                    }`}>
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-2">
                      {step.description}
                    </p>
                    <span className={`text-xs font-medium px-3 py-1 rounded-full transition-colors duration-300 ${
                      activeStep === index 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {step.duration}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Active Step Details */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-emerald-100 mb-16">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mr-4">
                <i className={`${steps[activeStep].icon} text-xl text-white`}></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {steps[activeStep].title}
                </h3>
                <p className="text-emerald-600 font-medium">
                  Süre: {steps[activeStep].duration}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  {steps[activeStep].description}
                </p>
                <h4 className="font-semibold text-gray-900 mb-4">
                  Bu Aşamada Yapılanlar:
                </h4>
                <ul className="space-y-2">
                  {steps[activeStep].details.map((detail, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-xs text-emerald-600"></i>
                      </div>
                      <span className="text-gray-600 text-sm">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-4">
                  Neden Bu Aşama Önemli?
                </h4>
                <div className="space-y-3">
                  {activeStep === 0 && (
                    <p className="text-gray-700 text-sm">
                      Doğru başlangıç, başarılı bir sürdürülebilirlik yolculuğunun temelidir. 
                      Mevcut durumunuzu anlamadan hedef belirlemek mümkün değildir.
                    </p>
                  )}
                  {activeStep === 1 && (
                    <p className="text-gray-700 text-sm">
                      Detaylı analiz olmadan yapılan iyileştirmeler genellikle yetersiz kalır. 
                      Veriye dayalı kararlar sürdürülebilirlik başarısının anahtarıdır.
                    </p>
                  )}
                  {activeStep === 2 && (
                    <p className="text-gray-700 text-sm">
                      Stratejisiz hareket etmek kaynak israfına yol açar. Net hedefler ve 
                      planlar olmadan sürdürülebilirlik çabaları dağınık kalır.
                    </p>
                  )}
                  {activeStep === 3 && (
                    <p className="text-gray-700 text-sm">
                      En iyi strateji bile uygulanmazsa değersizdir. Sistematik uygulama 
                      ve sürekli takip sürdürülebilirlik hedeflerine ulaşmayı garantiler.
                    </p>
                  )}
                  {activeStep === 4 && (
                    <p className="text-gray-700 text-sm">
                      Sürdürülebilirlik performansını paydaşlarla paylaşmak şeffaflık sağlar 
                      ve itibarınızı güçlendirir. Profesyonel raporlama zorunlulukları karşılar.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-3xl p-12 text-white relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
                <div className="absolute bottom-0 right-0 w-40 h-40 bg-white rounded-full translate-x-20 translate-y-20"></div>
                <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-white rounded-full opacity-50"></div>
              </div>
              
              <div className="relative z-10">
                <h3 className="text-3xl font-bold mb-4">
                  Sürdürülebilirlik Yolculuğunuza Bugün Başlayın
                </h3>
                <p className="text-emerald-100 mb-8 text-lg max-w-2xl mx-auto">
                  16+ yıllık deneyimimiz ve kanıtlanmış metodolojimizle işletmenizin 
                  sürdürülebilirlik hedeflerine ulaşmasına yardımcı oluyoruz.
                </p>
                
                {/* Benefits */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white"></i>
                    </div>
                    <span className="text-emerald-100">Ücretsiz İlk Konsültasyon</span>
                  </div>
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                      <i className="ri-team-line text-white"></i>
                    </div>
                    <span className="text-emerald-100">Uzman Ekip Desteği</span>
                  </div>
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                      <i className="ri-settings-3-line text-white"></i>
                    </div>
                    <span className="text-emerald-100">Özelleştirilmiş Çözümler</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={handleCallRequest}
                    className="bg-white text-emerald-600 px-8 py-4 rounded-xl font-semibold hover:bg-emerald-50 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    <div className="w-5 h-5 flex items-center justify-center mr-2 inline-flex">
                      <i className="ri-phone-line"></i>
                    </div>
                    Sizi Arayalım
                  </button>
                  <button
                    onClick={() => setIsAppointmentModalOpen(true)}
                    className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-emerald-600 transition-all duration-300 whitespace-nowrap cursor-pointer transform hover:-translate-y-1"
                  >
                    <div className="w-5 h-5 flex items-center justify-center mr-2 inline-flex">
                      <i className="ri-calendar-line"></i>
                    </div>
                    Randevu Al
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Appointment Modal */}
      <AppointmentModal 
        isOpen={isAppointmentModalOpen} 
        onClose={() => setIsAppointmentModalOpen(false)} 
      />
    </>
  );
}
